import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Column } from 'primereact/column'

import { DeliveryForecastItemSplit } from '../../../../../../data/models'
import {
  isRowSelectable,
  rowClassName,
} from '../../../../../../utils/helper/isRowSelectable'
import {
  ConfigurableTable,
  convertDeliveryForecastItems,
} from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import { useAllDeliveryForecastItemSplits } from '../../../../data'
import {
  AddSplitItemContainer,
  DeleteSplitItem,
  RejectSplitItems,
} from './dialogs'
import { EditSplitItemContainer } from './dialogs/EditSplitItem'
import { InfoSplitItem } from './dialogs/InfoSplitItem'

type Props = {
  deliveryForecastId: string
  isLoading: boolean
  isConfirmationActive: boolean
  itemId: string
  unit: string | undefined
  iconOnly?: boolean
}

export const SplitsTable: React.FC<Props> = ({
  deliveryForecastId,
  isLoading,
  isConfirmationActive,
  itemId,
  unit,
  iconOnly,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  const { data: deliveryForecastItemSplits, isLoading: isLoadingSplits } =
    useAllDeliveryForecastItemSplits(deliveryForecastId, itemId)

  const [selectedItems, setSelectedItems] = useState<
    DeliveryForecastItemSplit[] | null
  >()

  const headerTitle = t(
    'deliveryForecast.deliveryScheduleDetails.splits.table.title',
  )
  const headerActions = isConfirmationActive ? (
    <>
      <RejectSplitItems
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        deliveryForecastId={deliveryForecastId}
        itemId={itemId}
      />

      <AddSplitItemContainer
        deliveryForecastId={deliveryForecastId}
        itemId={itemId}
        unit={unit}
        items={deliveryForecastItemSplits!}
      />
    </>
  ) : undefined

  //  action template
  const actionBodyTemplate = (rowData: DeliveryForecastItemSplit) => {
    return (
      <>
        <div className="flex justify-content-end flex-nowrap">
          {rowData.Status !== 'ADDED' && isConfirmationActive && (
            <RejectSplitItems
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              rowData={rowData}
              deliveryForecastId={deliveryForecastId}
              itemId={itemId}
              iconOnly={iconOnly}
            />
          )}

          {rowData.Status === 'ADDED' && (
            <DeleteSplitItem
              deliveryForecastId={deliveryForecastId}
              itemId={itemId}
              itemSplit={rowData}
            />
          )}

          <InfoSplitItem
            deliveryForecastId={deliveryForecastId}
            itemId={itemId}
            rowData={rowData}
          />

          {isConfirmationActive && (
            <EditSplitItemContainer
              deliveryForecastId={deliveryForecastId}
              itemId={itemId}
              setSelectedItems={setSelectedItems}
              rowData={rowData}
            />
          )}
        </div>
      </>
    )
  }

  const buttons = (
    <Column key="buttons" body={actionBodyTemplate} align="right"></Column>
  )

  return (
    <>
      <h3 className="mt-6">
        {t('deliveryForecast.deliveryScheduleDetails.splits.title')}
      </h3>
      <ConfigurableTable
        config={
          config.deliveryForecast.deliveryScheduleDetails.splitsTable.table
        }
        data={convertDeliveryForecastItems(deliveryForecastItemSplits)}
        buttons={buttons}
        headerTitle={headerTitle}
        headerActions={headerActions}
        checkboxen={isConfirmationActive}
        selected={selectedItems}
        setSelected={setSelectedItems}
        isDataSelectable={isRowSelectable}
        rowClassName={rowClassName}
        isLoading={isLoading || isLoadingSplits}
        dataKey="ConfirmationDeliveryDate"
        stateKey=""
      />
    </>
  )
}
